import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography, styled, Radio, RadioGroup, FormControlLabel, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  StewartDialog,
  StewartDialogActions,
  StewartDialogContent,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";

import { borderRadius, colors, fontFamily, fontSize, fontWeight, gaps, gradients, iconSize, margin, padding } from "theme/defaultStyle";

import AddIcon from "theme/icons/AddIcon";

import SaveButton from "./buttons/SaveButton";
import BackButton from "./buttons/BackButton";

import {
  useSignatureProvider,
  useSignatureDispatchProvider,
} from "pages/signatures/providers/manage-signature/SignatureProvider";

import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import TextInputField from "controls/global/text-input-field/TextInputField";

import { FormProvider, useForm } from "react-hook-form";
import ActionButton from "controls/global/action-button";
import { IMG_MAX_HEIGHT, getImageTypeFromImageDataUrl } from "utils/signature/imageFile";
import StewartDrawZone from "controls/global/stewart-draw-zone";
import CustomTooltip from "controls/global/custom-tooltip";
import StyledInfoIcon from "theme/icons/StyledInfoIcon";
import SignatureLevel from "../signature-level";
import { DocumentType, SignatureLevelType } from "utils/data/enum";
import { FileRejection, FileWithPath } from "react-dropzone";
import CropImage from "controls/global/crop-image/CropImage";
import { ImagePreview } from "controls/global/crop-image/ImagePreview";
import { useSignatures } from "utils/context/SignaturesContext";
import useDebounce from "utils/custom-hooks/useDebounce";
import EditSignatureIcon from "theme/icons/EditSignatureIcon";
import TypeSignature from "../type-signature";
import { useConfigurationProvider } from "pages/signatures/providers/manage-signature/ConfigurationProvider";
import { getDuplicateAgencies, getSelectedAgencyNames } from "./utils";
import DuplicateSignaturesTooltip from "./DuplicateSignaturesTooltip";
import { getSignatureActionLabels } from "utils/services/utilsService";
import CircularProgressWithLabel from "controls/global/circular-progress-with-label";

const SIGNATURE_ERROR_MESSAGES = {
  SINGLE_AGENCY_DUPLICATE: "This Signature name already exists for agency: ",
  MULTIPLE_AGENCIES_DUPLICATE: "This signature name is already being used by multiple agencies: ",
} as const;

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "878px",
    minWidth: "878px",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 30px 0px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
  marginLeft: `${margin.xlarge}`,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const SignatureNameContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: gaps.large1,
});

const SignatureAccessContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
  width: "100%",
  "& > div:first-of-type": {
    flex: "1 1 95%",
    minWidth: 0,
  },
  "& > div:last-of-type": {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
  },
});

const signatureToolTipStyle = {
  fontSize: fontSize.small,
  fontFamily: fontFamily.inter,
  fontWeight: fontWeight.normal1,
}

const LoaderContainer = styled(Box)({
  marginTop: "16px",
  height: "201px",
  border: "1px dashed " + colors.blue19,
  borderRadius: borderRadius.small,
  background: colors.blue17,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
});

const StyledCircularProgress = styled(CircularProgress)({
  color: colors.blue01,
});

const StyledProgressStatus = styled(Typography)({
  color: colors.black02,
  fontWeight: fontWeight.normal2,
  fontSize: fontSize.large,
});

const DrawContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: gaps.small1,
});

const SignatureErrorMessage = styled(Typography)({
  color: colors.red01,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal1,
  paddingLeft: padding.medium1,
  paddingRight: padding.xlarge3,
  marginTop: margin.xsmall,

});


const ProgressWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1300
});

type EditableSignatureField = {
  name: string;
  mode: "UPLOAD" | "TYPE" | "DRAW";
  file: string;
  signatureLevelTypeCode: string;
  signatureDescription: string;
  fontFamilyID: string;
};

const StyledTypeSignatureText = styled(Typography)({
  margin: `${margin.xlarge1} ${margin.zero}`,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.normal1,
});

const StyledDialogActions = styled(StewartDialogActions)({
  display: "flex",
  justifyContent: "flex-end",
  padding: `${padding.large1} ${padding.large2}`,
});

const StyledTypeSignature = styled(TypeSignature)<{ disabled?: boolean }>(({ disabled=false }) => ({
  marginTop: margin.xlarge1,
  ...disabled && { pointerEvents: 'none'},
  opacity: disabled ? 0.6 : 1,
}));

type Props = {
  refreshPage?: () => void;
};


const SignatureDialog = ({refreshPage}:Props) => {
  const {
    state: { mode, open, isNameValid, signature, imageSource },
  } = useSignatureProvider();
  const [{ cachedSignature, loadingProgress }, { checkName },] = useSignatures();
  const { dispatch: signatureDispatch, dispatch } = useSignatureDispatchProvider();
  const {
    state: { nodes },
  } = useConfigurationProvider();

  const methods = useForm({
    defaultValues: {
      SignatureName: "",
      TypeSignature: "",
      signatureLevelTypeCode: signature.signatureLevelTypeCode || SignatureLevelType.AgencyWide,
    }
  });

  
  const [signatureNameField, setSignatureNameField] = useState("");
  const debouncedValue = useDebounce(signatureNameField, 500);


  const [title, setTitle] = useState("");
  const [openCropView, setOpenCropView] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(80);

  // Track data separately for each mode
  const [uploadedImage, setUploadedImage] = useState<{ file: string; imageType: string } | null>(null);
  const [drawnImage, setDrawnImage] = useState<{ file: string; imageType: string } | null>(null);
  const [typedImage, setTypedImage] = useState<{ file: string; imageType: string, fontFamilyID: string } | null>(null);

  const baselineRef = React.useRef<EditableSignatureField | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [isDialogReady, setIsDialogReady] = useState(false);

  const isFieldDisabled = () => {


    if (mode !== "ADD") {
      const actionConfig = getSignatureActionLabels(signature.isModify ?? false);

      return actionConfig.isFieldDisabled;
    }
    return false;
  }
  const duplicatedAgenciesRef = React.useRef<string[]>([]);

  const getImage = (imageSrc: string) => {
    const imageType = getImageTypeFromImageDataUrl(imageSrc);

    // Store in drawn image state
    setDrawnImage(imageSrc ? { file: imageSrc, imageType } : null);

    dispatch({
      type: "FILE_UPDATE",
      signatureFile: imageSrc,
      imageType: imageType,
    });
  };

  const reset = () => {
    signatureDispatch({ type: "SELECT_OPTION", imageSource: "UPLOAD" });
    setOpenCropView(false);
    setTempImageForCrop("");
    setUploadedImage(null);
    setSignatureNameField(""); // Reset local name
    setDrawnImage(null);
    setTypedImage(null);
    signatureDispatch({ type: "CLOSE" });
    setUploadProgress(80);
    methods.reset({
      SignatureName: "",
      TypeSignature: "",
      signatureLevelTypeCode: SignatureLevelType.AgencyWide,
    });
    baselineRef.current = null;
    setHasChanges(false);
  };

  useEffect(() => {
    if (!open) return;

    dispatch({
      type: "NAME_UPDATE",
      signatureName: signatureNameField,
      isCheckingName: true,
    });
  }, [mode, signatureNameField]);

  useEffect(() => {
    if (mode === "ADD" && (!signature.name || signature.name.trim() === "") && !signature.signatureDescription) {
      setSignatureNameField("");
      methods.reset({
        SignatureName: "",
        TypeSignature: "",
        signatureLevelTypeCode: SignatureLevelType.AgencyWide,
      });
    }
    if (!signature.file && mode === "ADD") {
      setUploadedImage(null);
      setDrawnImage(null);
      setTypedImage(null);
    }
  }, [signature.name, mode, signature.signatureDescription]);


  useEffect(() => {
    if (!open) {
      setIsDialogReady(false);
      return;
    }

    if (mode === "ADD") {
      setTitle("Add Signature");
      if (!imageSource) {
        signatureDispatch({ type: "SELECT_OPTION", imageSource: "UPLOAD" });
      }
      // Restore image states from signature when reopening
      if (signature.file) {
        if (imageSource === "UPLOAD") {
          setUploadedImage({ file: signature.file, imageType: signature.imageType || "" });
        } else if (imageSource === "DRAW") {
          setDrawnImage({ file: signature.file, imageType: signature.imageType || "" });
        } else if (imageSource === "TYPE") {
          setTypedImage({ file: signature.file, imageType: signature.imageType || "", fontFamilyID: signature.fontFamilyID || "" });
        }
      }
    } else if (mode === "EDIT") {
      // Only set dialog ready when we have the signature data
      if (signature.signatureID && signature.name && signature.file && loadingProgress === 100) {
        setIsDialogReady(true);
        setTitle(!signature.name ? "" : isFieldDisabled() ? "View Signature" : "Edit Signature");
        setSignatureNameField(signature.name || "");
        methods.setValue("SignatureName", signature.name);
        methods.setValue("TypeSignature", signature.signatureDescription || "");

        if (signature.file && imageSource) {
          const editMode = imageSource as "UPLOAD" | "TYPE" | "DRAW";

          if (editMode === "UPLOAD") {
            setUploadedImage({ file: signature.file, imageType: signature.imageType || "" });
          } else if (editMode === "DRAW") {
            setDrawnImage({ file: signature.file, imageType: signature.imageType || "" });
          } else if (editMode === "TYPE") {
            setTypedImage({ file: signature.file, imageType: signature.imageType || "", fontFamilyID: signature.fontFamilyID || "" });
          }
        }
      } else if (loadingProgress < 100) {
        // Explicitly set to not ready while loading
        setIsDialogReady(false);
      }
    }
  }, [open, mode, signature.signatureID, signature.name, signature.file, signature.imageType, signature.signatureDescription, signature.fontFamilyID, imageSource, loadingProgress]);


  useEffect(() => {
    if (
      open &&
      mode === "EDIT" &&
      signature.signatureID &&
      signature.name &&
      !baselineRef.current
    ) {
      baselineRef.current = {
        name: signature.name,
        mode: imageSource as "UPLOAD" | "TYPE" | "DRAW",
        file: signature.file || "",
        signatureLevelTypeCode: signature.signatureLevelTypeCode ?? '',
        signatureDescription: signature.signatureDescription || "",
        fontFamilyID: signature.fontFamilyID || "",
      };

      setHasChanges(false); // ✅ reset dirty
    }

    if (!open) {
      baselineRef.current = null;
      setHasChanges(false);
    }
  }, [
    open,
    mode,
    signature.signatureID,
    signature.name,
  ])

  // Update signature state when switching modes - only update if we have data for that mode
  useEffect(() => {
    if (!open) return;

    if (mode === "ADD") {
      if (imageSource === "UPLOAD") {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: uploadedImage?.file ?? "",
          imageType: uploadedImage?.imageType ?? "",
          fontFamilyID: typedImage?.fontFamilyID || "",
        });
      } else if (imageSource === "DRAW") {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: drawnImage?.file ?? "",
          imageType: drawnImage?.imageType ?? "",
        });
      } else if (imageSource === "TYPE") {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: typedImage?.file ?? "",
          imageType: typedImage?.imageType ?? "",
          fontFamilyID: typedImage?.fontFamilyID || "",
        });
      }
      return;
    }

    else if (mode === "EDIT") {
      if (imageSource === "UPLOAD" && uploadedImage) {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: uploadedImage.file,
          imageType: uploadedImage.imageType,
        });
      }

      else if (imageSource === "DRAW" && drawnImage) {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: drawnImage.file,
          imageType: drawnImage.imageType,
          fontFamilyID: typedImage?.fontFamilyID || "",
        });
      }

      else if (imageSource === "TYPE" && typedImage) {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: typedImage.file,
          imageType: typedImage.imageType,
          fontFamilyID: typedImage.fontFamilyID,
        });
      }
      else {
        dispatch({
          type: "FILE_UPDATE",
          signatureFile: "",
          imageType: "",
          fontFamilyID: "",
        });
      }
    }

  }, [imageSource, uploadedImage, drawnImage, typedImage, dispatch]);

  useEffect(() => {
    if (imageSource === "TYPE" && signature.fontFamilyID) {
      // When a font is selected in TYPE mode, update typedImage immediately
      setTypedImage(prev => ({
        file: signature.file || prev?.file || "",
        imageType: signature.imageType || prev?.imageType || "",
        fontFamilyID: signature.fontFamilyID,
      }));
    }
  }, [signature.fontFamilyID, imageSource, signature.file, signature.imageType]);

  const calculateDirty = (partial?: Partial<EditableSignatureField>) => {
    if (!baselineRef.current) return false;

    const currentName = partial?.name ?? signatureNameField ?? "";
    const currentMode = partial?.mode ?? imageSource;
    const currentFile = partial?.file ?? (
      imageSource === "UPLOAD"
        ? uploadedImage?.file || ""
        : imageSource === "DRAW"
          ? drawnImage?.file || ""
          : ""
    );
    const currentSignatureLevelTypeCode = partial?.signatureLevelTypeCode ?? signature.signatureLevelTypeCode ?? '';
    const currentSignatureDescription = signature.signatureDescription || '';
    const currentFontFamilyID = signature.fontFamilyID || '';

    const current: EditableSignatureField = {
      name: currentName,
      mode: currentMode as "UPLOAD" | "TYPE" | "DRAW",
      file: currentFile,
      signatureLevelTypeCode: currentSignatureLevelTypeCode,
      signatureDescription: currentSignatureDescription,
      fontFamilyID: currentFontFamilyID,
    };

    const nameChanged = current.name !== baselineRef.current.name;
    const modeChanged = current.mode !== baselineRef.current.mode;
    const fileChanged = current.file !== baselineRef.current.file;
    const levelChanged = current.signatureLevelTypeCode !== baselineRef.current.signatureLevelTypeCode;
    const descriptionChanged = current.signatureDescription !== baselineRef.current.signatureDescription;
    const fontChanged = current.fontFamilyID !== baselineRef.current.fontFamilyID;


    const isTypeMode = current.mode === "TYPE";
    const modeSpecificChanges = isTypeMode ? (descriptionChanged || fontChanged) : fileChanged;

    return nameChanged || modeChanged || levelChanged || modeSpecificChanges;
  };

  const handleOnUpload = async (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
    if (fileRejections && fileRejections.length > 0) {
      console.error("File rejection errors:", fileRejections);
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      try {
        setIsUploading(true);
        setUploadProgress(80);
        const reader = new FileReader();

        reader.onload = async (e) => {
          const imageDataUrl = e.target?.result as string;

          const duration = 2000;
          const interval = 50;
          const steps = duration / interval;
          const increment = 20 / steps;

          for (let i = 0; i <= steps; i++) {
            const newProgress = Math.min(80 + (i * increment), 100);
            setUploadProgress(Math.round(newProgress));
            await new Promise(resolve => setTimeout(resolve, interval));
          }

          setTempImageForCrop(imageDataUrl);
          setIsUploading(false);
          setUploadProgress(80);
          setOpenCropView(true);
        };

        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          setIsUploading(false);
          setUploadProgress(80);
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
        setIsUploading(false);
        setUploadProgress(80);
      }
    }
  };

  const handleCrop = (croppedImage: string) => {
    const imageType = getImageTypeFromImageDataUrl(croppedImage);

    // Store in uploaded image state
    setUploadedImage({ file: croppedImage, imageType });

    dispatch({
      type: "FILE_UPDATE",
      signatureFile: croppedImage,
      imageType: imageType,
    });

    setOpenCropView(false);
    setTempImageForCrop("");
  };

  const handleCropCancel = () => {
    setOpenCropView(false);
    setTempImageForCrop("");
  };

  const handleClearImage = () => {
    if (imageSource === "UPLOAD") {
      setUploadedImage(null);
    }

    dispatch({
      type: "FILE_UPDATE",
      signatureFile: "",
      imageType: "",
    });
  };

  const handleSignatureNameChange = (event: React.ChangeEvent<any>) => {
    const text = String(event.target.value);
    setSignatureNameField(text);
  };

  const handleSignatureDescriptionChange = (event: React.ChangeEvent<any>) => {
    const text = String(event.target.value);
    dispatch({
      type: "TYPED_SIGNATURE_UPDATE",
      signatureDescription: text,
    });

    const isDirty = calculateDirty({ signatureDescription: text });
    setHasChanges(isDirty);
  };

  const generatePayloadForCheckName = (mode: string | undefined, nameToCheck: string) => {
    let payload: any = {};

    if (mode === "ADD") {
      payload = {
        signatureImageID: 0,
        signatureImageName: nameToCheck.trim(),
        signatureConfiguration: nodes || undefined,
      };
    } else if (mode === 'EDIT') {

      payload = {
        signatureImageName: nameToCheck.trim(),
        signatureConfiguration: cachedSignature?.signatureConfiguration,
        signatureImageID: cachedSignature?.signatureImageID,
      }
    }

    return payload;
  };

  const checkNameDebounced = React.useCallback(
    (nameToCheck: string) => {
      if (!nameToCheck.trim()) return;

      const payload = generatePayloadForCheckName(mode, nameToCheck);
      checkName(payload)
        .then((signatureDetails) => {
          if (!signatureDetails || !signatureDetails.signatureConfiguration)
            return;

          const duplicateAgencies = getDuplicateAgencies(
            signatureDetails.signatureConfiguration
          );

          // No duplicates found - name is valid
          if (duplicateAgencies.length === 0) {
            dispatch({
              type: "SIG_VALIDATE",
              isCheckingName: false,
              isNameValid: true,
            });

            duplicatedAgenciesRef.current = [];
            return;
          }

          const hasAllMyAgencies = duplicateAgencies.some(
            (agency) => agency.agencyName === "All My Agencies"
          );

          if (mode === "ADD") {
            // In ADD mode, any duplicates mean the name is invalid

            dispatch({
              type: "SIG_VALIDATE",
              isCheckingName: false,
              isNameValid: false,
            });

            const selectedAgencyNames = getSelectedAgencyNames(
              payload.signatureConfiguration
            );

            if (hasAllMyAgencies) {
              // If "All My Agencies" is a duplicate, all selected agencies are duplicates
              duplicatedAgenciesRef.current = selectedAgencyNames;
              return;
            }

            duplicatedAgenciesRef.current = duplicateAgencies
              .filter((da) => selectedAgencyNames.includes(da.agencyName))
              .map((da) => da.agencyName);
            return;
          } else if (mode === "EDIT") {
            if (
              hasAllMyAgencies &&
              cachedSignature?.signatureImageName === nameToCheck
            ) {
              // Allow if the duplicate is "All My Agencies" and the name matches the cached signature name
              dispatch({
                type: "SIG_VALIDATE",
                isCheckingName: false,
                isNameValid: true,
              });

              duplicatedAgenciesRef.current = [];
              return;
            }

            const selectedAgencyNames = getSelectedAgencyNames(
              payload.signatureConfiguration
            );

            if (
              hasAllMyAgencies &&
              nameToCheck !== cachedSignature?.signatureImageName
            ) {
              // Name has changed and "All My Agencies" is a duplicate
              dispatch({
                type: "SIG_VALIDATE",
                isCheckingName: false,
                isNameValid: false,
              });
              duplicatedAgenciesRef.current = selectedAgencyNames;
              return;
            }

            const duplicateInSelectedAgencies = duplicateAgencies.filter(
              (da) => {
                return selectedAgencyNames.includes(da.agencyName);
              }
            );

            if (duplicateInSelectedAgencies.length === 0) {
              // No duplicates in selected agencies
              dispatch({
                type: "SIG_VALIDATE",
                isCheckingName: false,
                isNameValid: true,
              });
              duplicatedAgenciesRef.current = [];
              return;
            }

            if (
              nameToCheck !== cachedSignature?.signatureImageName &&
              duplicateInSelectedAgencies.length > 0
            ) {
              // Name has changed and there are duplicates in selected agencies
              dispatch({
                type: "SIG_VALIDATE",
                isCheckingName: false,
                isNameValid: false,
              });

              duplicatedAgenciesRef.current = duplicateInSelectedAgencies.map(
                (da) => da.agencyName
              );
              return;
            }
          }
        })
        .catch((error) => {
          console.error("Error checking signature name:", error);
        });
    },

    [dispatch, checkName, mode, cachedSignature, nodes]
  );

  const handleImageDoubleClick = () => {
    if (imageSource === "UPLOAD" && uploadedImage?.file && mode === "EDIT") {
      setTempImageForCrop(uploadedImage.file);
      setOpenCropView(true);
    }
  };

  useEffect(() => {
    const trimmedName = debouncedValue?.trim();
    if (!trimmedName || !open) return;
    if (
      mode === "EDIT" &&
      trimmedName.toLowerCase() ===
      cachedSignature?.signatureImageName?.trim()?.toLowerCase()
    ) {
      dispatch({
        type: "SIG_VALIDATE",
        isCheckingName: false,
        isNameValid: true,
      });
      duplicatedAgenciesRef.current = [];
      return;
    }

    checkNameDebounced(trimmedName);
  }, [checkNameDebounced, debouncedValue, open]);

  // Track signature mode changes
  useEffect(() => {
    if (mode === "EDIT" && baselineRef.current && open) {
      const isDirty = calculateDirty({ mode: imageSource as "UPLOAD" | "TYPE" | "DRAW" });
      setHasChanges(isDirty);
    }
  }, [imageSource, mode, open, calculateDirty]);

  // Track file changes
  useEffect(() => {
    if (mode === "EDIT" && baselineRef.current && open) {
      const currentFile = imageSource === "UPLOAD"
        ? uploadedImage?.file || ""
        : imageSource === "DRAW"
          ? drawnImage?.file || ""
          : "";

      const isDirty = calculateDirty({ file: currentFile });
      setHasChanges(isDirty);
    }
  }, [uploadedImage, drawnImage, mode, open, calculateDirty, imageSource]);

  // Track signature level changes
  useEffect(() => {
    if (mode === "EDIT" && baselineRef.current && open) {
      const isDirty = calculateDirty({ signatureLevelTypeCode: signature.signatureLevelTypeCode ?? '' });
      setHasChanges(isDirty);
    }
  }, [signature.signatureLevelTypeCode, mode, open, calculateDirty]);

  const typeSignatureValue = methods.watch("TypeSignature");

  useEffect(() => {
    if (mode === "EDIT" && baselineRef.current && imageSource === "TYPE") {
      const isDirty = calculateDirty({
        signatureDescription: typeSignatureValue ?? "",
        fontFamilyID: signature.fontFamilyID || "",
      });
      setHasChanges(isDirty);
    }
  }, [typeSignatureValue, mode, imageSource, calculateDirty, signature.fontFamilyID]);


  const renderContent = () => {
    const SignatureNameInput = (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <SignatureNameContainer>
          <TextInputField
            name="SignatureName"
            placeholder="Signature Name"
            label="Signature Name"
            onChange={handleSignatureNameChange}
            error={!isNameValid}
            disabled={isFieldDisabled()}
            maxLength={100}
          />
          <CustomTooltip
            title={`The Signature Name is the name that will appear in the dropdown when you add a signature to a product.`}
            placement="left"
            aria-label="signature-name-info"
            arrow
            componentsProps={{
              tooltip: {
                sx: signatureToolTipStyle
              }
            }}
          >
            <StyledInfoIcon />
          </CustomTooltip>
        </SignatureNameContainer>

        {!isNameValid && (
          <SignatureErrorMessage>
            {duplicatedAgenciesRef.current.length === 1 && SIGNATURE_ERROR_MESSAGES.SINGLE_AGENCY_DUPLICATE + duplicatedAgenciesRef.current[0]}
            {duplicatedAgenciesRef.current.length > 1 && <DuplicateSignaturesTooltip duplicateAgencies={duplicatedAgenciesRef.current} title={`${SIGNATURE_ERROR_MESSAGES.MULTIPLE_AGENCIES_DUPLICATE} ${duplicatedAgenciesRef.current.join(", ")}`} />}
          </SignatureErrorMessage>
        )}
      </Box>
    );

    const SignatureLevelInput = (
      <SignatureAccessContainer>
        <Box>
          <SignatureLevel key="LEVEL:4" disabledField={() => isFieldDisabled()} />
        </Box>
        <Box mt={2.5}>
          <CustomTooltip
            title={`Select ‘User Only’ to make this signature visible only to you, or ‘Agency‑Wide’ to make it available to all users with access to the selected agencies.`}
            placement="left"
            aria-label="signature-level-info"
            arrow
            componentsProps={{
              tooltip: {
                sx: signatureToolTipStyle
              }
            }}
          >
            <StyledInfoIcon />
          </CustomTooltip>
        </Box>
      </SignatureAccessContainer>
    );

    switch (imageSource) {
      case "UPLOAD":
        return (
          <Box sx={{ width: "100%" }}>
            {SignatureNameInput}
            {isUploading ? (
              <LoaderContainer>
                <StyledCircularProgress
                  value={uploadProgress}
                  size={80}
                  thickness={4}
                />
                <StyledProgressStatus>
                  {`${uploadProgress} %`}
                </StyledProgressStatus>
              </LoaderContainer>
            ) : !uploadedImage?.file ? (
              <UploadDropZone
                handleOnUpload={handleOnUpload}
                documentType={DocumentType.SIGNATURE}
              />
            ) : (
              <ImagePreview
                signature={{ ...signature, file: uploadedImage.file, imageType: uploadedImage.imageType }}
                handleClearImage={handleClearImage}
                handleImageDoubleClick={handleImageDoubleClick}
                disabled={isFieldDisabled()}
              />
            )}
            {SignatureLevelInput}
          </Box>
        );
      case "TYPE":
        return (
          <Box sx={{ width: "100%" }}>
            {SignatureNameInput}
            <StyledTypeSignatureText>
              In the Type Signature field, enter the name as it should appear on the product. Then choose a font style
              below.
            </StyledTypeSignatureText>
            <TextInputField
              name="TypeSignature"
              placeholder="Type Signature"
              label="Type Signature"
              onChange={handleSignatureDescriptionChange}
              disabled={isFieldDisabled()}
              maxLength={200}
            />
            <StyledTypeSignature disabled={isFieldDisabled()} />
            {SignatureLevelInput}
          </Box>
        );
      case "DRAW":
        return (
          <DrawContainer>
            {SignatureNameInput}
            <StewartDrawZone
              width={720}
              height={IMG_MAX_HEIGHT}
              getImage={getImage}
              defaultImage={drawnImage?.file || ""}
              disabled={isFieldDisabled()}
            />
            <Box mt={-1}>
              {SignatureLevelInput}
            </Box>
          </DrawContainer>
        );
      default:
        return null;
    }
  };

  const showLoader = () => {
    return (<ProgressWrapper><CircularProgressWithLabel value={loadingProgress} /></ProgressWrapper>)
  }
  return (
    <>
      {mode === "EDIT" && !isDialogReady ?  showLoader() : <FormProvider {...methods}>
        <StyledDialog
          open={open}
          onClose={reset}
          maxWidth="xl"
        
        >
          {!isDialogReady && mode === "EDIT" ? <ProgressWrapper><CircularProgressWithLabel value={loadingProgress} /></ProgressWrapper> : <><StyledDialogTitle>
            <IconContainer>
              {mode === "ADD" ? <AddIcon /> : <EditSignatureIcon />}
            </IconContainer>
            <Typography>{title}</Typography>
            <CloseIconContainer>
              <IconButton
                className="iconButton"
                onClick={reset}
              >
                <CloseIcon />
              </IconButton>
            </CloseIconContainer>
          </StyledDialogTitle>
            <StewartDialogContent>
              {/* Radio group for Upload / Type / Draw */}
              <RadioGroup
                sx={{ marginLeft: margin.small2 }}
                row
                value={imageSource}
                onChange={(e) => {
                  signatureDispatch({ type: "SELECT_OPTION", imageSource: e.target.value });
                }}
              >
                <FormControlLabel
                  disabled={isFieldDisabled()}
                  value="UPLOAD"
                  control={<Radio />}
                  label="Upload"
                />
                <FormControlLabel
                  disabled={isFieldDisabled()}
                  value="TYPE"
                  control={<Radio />}
                  label="Type"
                />
                <FormControlLabel
                  disabled={isFieldDisabled()}
                  value="DRAW"
                  control={<Radio />}
                  label="Draw"
                />
              </RadioGroup>

              {/* Render selected content */}
              <Box sx={{ mt: 2 }}>{renderContent()}</Box>
            </StewartDialogContent>
            <StyledDialogActions>
              {/* <NextButton activeStep="CONFIG" /> */}
              <ActionButton
                variant="outlined"
                color="secondary"
                onClick={reset}
              >
                {"Cancel"}
              </ActionButton>
              {mode === "ADD" && <BackButton activeStep="CONFIG" />}

              <SaveButton refreshPage={refreshPage} resetProviders={reset} hasChanges={hasChanges} />
            </StyledDialogActions></>}


        </StyledDialog>

      </FormProvider>}


      {/* Crop Image Dialog */}
      <CropImage
        imageUrl={tempImageForCrop}
        open={openCropView}
        onCrop={handleCrop}
        onCancel={handleCropCancel}
        renderNewSignatureStyledDialog={true}
      />
    </>
  );
};

export default SignatureDialog;
